import * as THREE from 'three';
import { PuzzleElement } from './PuzzleElement';

export class BreakableWall extends PuzzleElement {
  private isBroken: boolean = false;
  private breakDistance: number = 3;
  private fragments: THREE.Mesh[] = [];
  private fragmentVelocities: THREE.Vector3[] = [];
  private scene: THREE.Scene;
  
  constructor(position: THREE.Vector3, scene: THREE.Scene, size?: THREE.Vector3) {
    super(position);
    this.scene = scene;
    
    if (size) {
      // Resize the wall
      (this.mesh as THREE.Mesh).geometry.dispose();
      (this.mesh as THREE.Mesh).geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    }
  }
  
  protected createMesh(): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(2, 3, 0.5);
    const material = new THREE.MeshStandardMaterial({ 
      color: 0xa0522d,
      roughness: 0.9,
      metalness: 0.1
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    // Add cracks texture or lines to indicate breakability
    const edgeGeometry = new THREE.EdgesGeometry(geometry);
    const edgeMaterial = new THREE.LineBasicMaterial({ color: 0x3d2817 });
    const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
    mesh.add(edges);
    
    return mesh;
  }
  
  public update(deltaTime: number, _playerPosition: THREE.Vector3): void {
    // Animate fragments if broken
    if (this.isBroken) {
      for (let i = 0; i < this.fragments.length; i++) {
        const fragment = this.fragments[i];
        const velocity = this.fragmentVelocities[i];
        
        // Apply gravity
        velocity.y -= 10 * deltaTime;
        
        // Move fragment
        fragment.position.add(velocity.clone().multiplyScalar(deltaTime));
        fragment.rotation.x += deltaTime * 2;
        fragment.rotation.z += deltaTime * 3;
        
        // Fade out and remove when below ground
        if (fragment.position.y < -2) {
          this.scene.remove(fragment);
        }
      }
    }
  }
  
  public tryBreak(playerPosition: THREE.Vector3, _direction: THREE.Vector3): boolean {
    if (this.isBroken) return false;
    
    const distance = playerPosition.distanceTo(this.position);
    
    if (distance < this.breakDistance) {
      this.break();
      return true;
    }
    
    return false;
  }
  
  private break(): void {
    this.isBroken = true;
    this.mesh.visible = false;
    
    // Create fragments
    const numFragments = 8;
    const fragmentSize = 0.4;
    
    for (let i = 0; i < numFragments; i++) {
      const geometry = new THREE.BoxGeometry(
        fragmentSize + Math.random() * 0.3,
        fragmentSize + Math.random() * 0.3,
        fragmentSize + Math.random() * 0.2
      );
      
      const material = new THREE.MeshStandardMaterial({
        color: 0xa0522d,
        roughness: 0.9
      });
      
      const fragment = new THREE.Mesh(geometry, material);
      fragment.position.copy(this.position);
      fragment.position.x += (Math.random() - 0.5) * 1.5;
      fragment.position.y += (Math.random() - 0.5) * 2;
      fragment.position.z += (Math.random() - 0.5) * 0.5;
      
      fragment.castShadow = true;
      
      this.scene.add(fragment);
      this.fragments.push(fragment);
      
      // Random velocity
      this.fragmentVelocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 5,
        Math.random() * 3 + 2,
        (Math.random() - 0.5) * 5
      ));
    }
  }
  
  public isBrokenState(): boolean {
    return this.isBroken;
  }
  
  public getCollisionBox(): THREE.Box3 | null {
    if (!this.isBroken) {
      return new THREE.Box3().setFromObject(this.mesh);
    }
    return null;
  }
}
